/** @format */

import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore/lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth, db } from "senf-shared";
import { createUserFromProviderInDatabase } from "senf-shared/firebase/createUserFromProviderInDatabase";
import { useIsMobile } from "../../../hooks";
import Button from "../../core/button/Button";
import Icon from "../../core/icon/Icon";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import SimpleAuthOptions from "./SimpleAuthOptions";

const LoggingInModal = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { closeModal, openModal } = useModals();

  // Auto-detect magic link and manage state internally
  const [currentState, setCurrentState] = useState<
    "signingIn" | "success" | "error"
  >("signingIn");
  const [currentSuccessType, setCurrentSuccessType] = useState<
    "login" | "register"
  >("login");
  const [errorTitle, setErrorTitle] = useState<string | undefined>(undefined);
  const [errorDescription, setErrorDescription] = useState<string | undefined>(
    undefined,
  );
  const processingRef = useRef(false);

  console.log("🎨 LoggingInModal rendering with state:", currentState, {
    title: errorTitle,
    description: errorDescription,
    successType: currentSuccessType,
  });

  // Magic link processing
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (processingRef.current) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    processingRef.current = true;


    (async () => {
      try {
        setCurrentState("signingIn");

        // IMPORTANT: Extract email BEFORE any URL cleaning
        // Get email from URL parameters (for cross-browser magic links)
        const emailFromUrl = new URLSearchParams(window.location.search).get(
          "email",
        );

        console.log("🔍 Magic link email debugging:", {
          emailFromUrl,
          currentUrl: window.location.href,
          allParams: Object.fromEntries(
            new URLSearchParams(window.location.search),
          ),
        });

        // Check if we have a valid email
        if (!emailFromUrl) {
          throw new Error("No email found in URL");
        }

        // Try to sign in with the magic link
        // Firebase will handle the validation and throw appropriate errors
        const userCredential = await signInWithEmailLink(
          auth,
          emailFromUrl,
          window.location.href,
        );

        // Wait a moment for auth state to update
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Handle user document in Firestore
        const user = userCredential.user || auth.currentUser;
        if (user?.uid) {
          const userRef = doc(db, `users/${user.uid}`);
          const userDoc = await getDoc(userRef);

          if (!userDoc.exists()) {
            const formikStore = {
              values: {
                projectId: null,
                referrer: document.referrer || "",
                utm_id:
                  new URLSearchParams(window.location.search).get("utm_id") ||
                  "",
              },
            };
            try {
              await createUserFromProviderInDatabase(db, user, formikStore);
              await user.reload();
            } catch (error) {
              console.error(
                "❌ Magic link: Failed to create user document:",
                error,
              );
              throw error;
            }
          } else {
            console.log("✅ Magic link: User document already exists");
          }
        }

        // Clean URL after extracting all needed parameters
        window.history.replaceState(null, "", window.location.pathname);

        // Show success (default to login)
        setCurrentSuccessType("login");
        setCurrentState("success");

        // Auto-close after 3 seconds
        setTimeout(() => {
          closeModal();
        }, 3000);
      } catch (error: unknown) {
        console.error("Magic link error:", error);

        // Clean URL to remove magic link parameters
        window.history.replaceState(null, "", window.location.pathname);

        // Show error
        const firebaseError = error as { code?: string };

        if (
          firebaseError.code === "auth/invalid-email" ||
          (error as Error).message === "No email found in URL"
        ) {
          setErrorTitle("E-Mail-Adresse nicht gefunden");
          setErrorDescription(
            "Die E-Mail-Adresse konnte nicht gefunden werden. Bitte stelle sicher, dass du den Link in demselben Browser verwendest, in dem du dich registriert hast, oder gib deine E-Mail-Adresse erneut ein.",
          );
        } else if (firebaseError.code === "auth/invalid-action-code") {
          setErrorTitle("Link abgelaufen");
          setErrorDescription(
            "Dieser Anmeldelink ist abgelaufen oder wurde bereits verwendet. Bitte fordere einen neuen Link an.",
          );
        } else {
          setErrorTitle("Anmeldefehler");
          setErrorDescription(
            "Bei der Anmeldung ist ein Fehler aufgetreten. Bitte versuche es erneut.",
          );
        }
        setCurrentState("error");
      }
    })();
  }, [closeModal]);

  const handleRetry = () => {
    processingRef.current = false;

    // Close current modal and open SimpleAuthOptions
    closeModal();
    setTimeout(() => {
      openModal(<SimpleAuthOptions />, {
        swipe: !!isMobile,
        transform: false,
      });
    }, 100);
  };

  if (currentState === "signingIn") {
    return (
      <div className="px-4 py-6 flex flex-col items-center">
        <Icon
          name="loading"
          className="mx-auto mb-6 h-10 w-10 text-icon-default"
        />
        <h2 className="heading-xl font-black mb-2">
          {t("auth:signingIn", "Wird angemeldet…")}
        </h2>
      </div>
    );
  }

  if (currentState === "success") {
    return (
      <div className="px-4 py-6 flex flex-col items-center justify-center">
        <Icon
          name="checkmark-circle-fill"
          className="mx-auto mb-6 h-10 w-10 text-icon-success"
        />
        <h2 className="heading-xl font-black">
          {currentSuccessType === "register"
            ? t("auth:registerSuccess", "Registrierung erfolgreich")
            : t("auth:loginSuccess", "Anmeldung erfolgreich")}
        </h2>
      </div>
    );
  }

  if (currentState === "error") {
    return (
      <div className="px-4 pb-6 flex flex-col items-center">
        <Icon
          name="alert-circle"
          className="mx-auto mb-6 h-10 w-10 text-icon-error"
        />
        <h2 className="heading-xl font-black mb-2">
          {errorTitle || t("auth:linkError.title", "Anmeldefehler")}
        </h2>
        <p className="body-md text-text-subdued mb-6 text-center">
          {errorDescription ||
            t(
              "auth:linkError.description",
              "Bei der Anmeldung ist ein Fehler aufgetreten.",
            )}
        </p>
        <div className="w-full space-y-3">
          <Button
            variant="primary"
            size="xl"
            width="max"
            text={t("auth:linkError.tryAgainButton", "Erneut versuchen")}
            classNameWrapper="w-full"
            onClick={handleRetry}
          />
        </div>
      </div>
    );
  }

  return null;
};

export default LoggingInModal;
